import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card } from '@/components/ui/card';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, MousePointerClick, Eye, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AIInsightsWidget, AIQuickActions } from '@/components/AIInsightsWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Dashboard = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/overview`, {
          withCredentials: true
        });
        setOverview(response.data);
      } catch (error) {
        console.error('Error fetching dashboard:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const statCards = [
    {
      title: 'Total Spend',
      value: `$${overview?.total_spend?.toLocaleString() || '0'}`,
      icon: DollarSign,
      change: '+12.5%',
      testId: 'stat-total-spend'
    },
    {
      title: 'Total Impressions',
      value: overview?.total_impressions?.toLocaleString() || '0',
      icon: Eye,
      change: '+8.2%',
      testId: 'stat-impressions'
    },
    {
      title: 'Total Clicks',
      value: overview?.total_clicks?.toLocaleString() || '0',
      icon: MousePointerClick,
      change: '+15.3%',
      testId: 'stat-clicks'
    },
    {
      title: 'Avg. CTR',
      value: `${overview?.avg_ctr || '0'}%`,
      icon: TrendingUp,
      change: '+0.3%',
      testId: 'stat-ctr'
    },
  ];

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="dashboard-page">
        {/* GIOS Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-light tracking-tight">Infrastructure Overview</h1>
            <span className="text-[10px] px-2 py-0.5 rounded tracking-wider uppercase" 
                  style={{ backgroundColor: 'rgba(212, 168, 75, 0.1)', color: '#B8975F', border: '1px solid rgba(212, 168, 75, 0.2)' }}>
              Private Beta Access
            </span>
          </div>
          <p className="text-sm text-muted-foreground">Your centralized performance command center.</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, idx) => (
            <Card key={idx} data-testid={stat.testId} className="p-6 hover:border-primary/50 transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <stat.icon className="w-5 h-5 text-primary" />
                </div>
                <span className="text-sm text-green-500 font-medium">{stat.change}</span>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                <p className="text-2xl font-bold">{stat.value}</p>
              </div>
            </Card>
          ))}
        </div>

        {/* Section: Cross-Platform Intelligence */}
        <div className="mb-6">
          <h2 className="text-lg font-medium mb-4 text-muted-foreground">Cross-Platform Intelligence</h2>
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Performance Trend */}
          <Card className="p-6">
            <h3 className="text-base font-medium mb-4">Performance Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={overview?.performance_trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))' 
                  }} 
                />
                <Legend />
                <Line type="monotone" dataKey="spend" stroke="hsl(var(--primary))" strokeWidth={2} />
                <Line type="monotone" dataKey="clicks" stroke="hsl(var(--accent))" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {/* AI Insights Widget */}
          <AIInsightsWidget />
        </div>

        {/* Section: Governance & Compliance */}
        <div className="mb-6">
          <h2 className="text-lg font-medium mb-4 text-muted-foreground">Governance & Platform Performance</h2>
        </div>

        {/* Platform Performance & AI Quick Actions */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Platform Performance */}
          <Card className="p-6 lg:col-span-2">
            <h3 className="text-base font-medium mb-4">Platform Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={overview?.platform_performance || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="platform" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))' 
                  }} 
                />
                <Legend />
                <Bar dataKey="spend" fill="hsl(var(--primary))" />
                <Bar dataKey="conversions" fill="hsl(var(--accent))" />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* AI Quick Actions */}
          <div className="space-y-4">
            <AIQuickActions />
            <Card className="p-4">
              <h3 className="text-sm font-medium mb-2">Infrastructure Status</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Active Campaigns</span>
                  <span className="text-lg font-bold">{overview?.active_campaigns || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Connected Platforms</span>
                  <span className="text-lg font-bold">{overview?.connected_platforms || 0}</span>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Help Card */}
        <div className="grid md:grid-cols-1 gap-6">
          <Card className="p-6 bg-primary text-primary-foreground">
            <h3 className="text-base font-medium mb-2">MediaKshetra Advisory</h3>
            <p className="text-sm mb-4 opacity-90">
              Contact our team for structured advisory on campaign optimization and performance governance.
            </p>
            <button className="px-4 py-2 bg-primary-foreground text-primary rounded-md hover:opacity-90 transition-opacity">
              Contact Support
            </button>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
